import { OrganizationKind } from "@/core/context/__generated__/ActiveOrganizationContextFragment.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { DotPaths } from "@utils/typeUtils"

/**
 * Determines whether a UI feature should be shown, initially only based on organization kind
 *
 * Certain features are only available to specific kinds of organizations,
 * such as "community" or "workspace". This hook uses a static configuration
 * to decide whether a given feature should be visible for the currently
 * active organization.
 *
 * The primary purpose of this mechanism is to provide a more streamlined, simple
 * experience for "workspace" organizations who do not benefit from many of the
 * customization and monetization settings.
 *
 * This is typically used to hide or reveal UI elements conditionally.
 *
 * NOTE: This is distinct from entitlements, paywall features, or feature flags,
 * which are managed separately in the platform and can differ for each individual org.
 */

type ScopedFeaturesConfigContext = {
  isChatEnabled: boolean
}

type ScopedFeaturesConfigOpts = {
  context: ScopedFeaturesConfigContext
}

type ScopedFeaturesConfig = {
  organization: {
    monetization: boolean
    "public-pages": boolean
    settings: {
      access: boolean
      search: boolean
      registration: boolean
    }
    dashboard: {
      "view-community-button": boolean
    }
    products: {
      templates: boolean
    }
    appearance: {
      labels: boolean
      favicon: boolean
    }
    integrations: {
      "google-analytics": boolean
      "google-tag-manager": boolean
      "facebook-pixel": boolean
    }
    insights: {
      chat: boolean
    }
  }
  product: {
    "public-access": boolean
    "type-tag": boolean
  }
}

type GetScopedFeaturesConfig = (opts: ScopedFeaturesConfigOpts) => ScopedFeaturesConfig

const COMMUNITY_SCOPED_FEATURES: GetScopedFeaturesConfig = () => ({
  organization: {
    monetization: true,
    "public-pages": true,
    settings: {
      access: true,
      search: true,
      registration: true,
    },
    dashboard: {
      "view-community-button": true,
    },
    products: {
      templates: true,
    },
    appearance: {
      labels: true,
      favicon: true,
    },
    integrations: {
      "google-analytics": true,
      "google-tag-manager": true,
      "facebook-pixel": true,
    },
    insights: {
      chat: true,
    },
  },
  product: {
    "public-access": true,
    "type-tag": true,
  },
})

const WORKSPACE_SCOPED_FEATURES: GetScopedFeaturesConfig = ({ context }) => ({
  organization: {
    monetization: false,
    "public-pages": false,
    settings: {
      access: false,
      search: false,
      registration: false,
    },
    dashboard: {
      "view-community-button": false,
    },
    products: {
      templates: false,
    },
    appearance: {
      labels: false,
      favicon: false,
    },
    integrations: {
      "google-analytics": false,
      "google-tag-manager": false,
      "facebook-pixel": false,
    },
    insights: {
      chat: context.isChatEnabled,
    },
  },
  product: {
    "public-access": false,
    "type-tag": false,
  },
})

export type ScopedFeatureType = DotPaths<ScopedFeaturesConfig>

const ORGANIZATION_SCOPED_FEATURES: Record<OrganizationKind, GetScopedFeaturesConfig> = {
  community: COMMUNITY_SCOPED_FEATURES,
  workspace: WORKSPACE_SCOPED_FEATURES,
  "%future added value": COMMUNITY_SCOPED_FEATURES,
}

export function useScopedFeatures() {
  const activeOrganization = useActiveOrganization()

  if (!activeOrganization) {
    throw new Error("useScopedFeatures must be used within ActiveOrganizationContext")
  }

  const context: ScopedFeaturesConfigContext = {
    isChatEnabled: activeOrganization.isChatEnabled,
  }

  const features = ORGANIZATION_SCOPED_FEATURES[activeOrganization.kind]({ context })

  return (feature: ScopedFeatureType) => {
    return feature
      .split(".")
      .reduce((obj, key) => (obj as any)?.[key], features) as unknown as boolean
  }
}
